// AgentAuditReport — daily evaluation report for the AI Chat agent.
//
// Mirrors the KnowledgeAdmin page shell exactly: same hero, same topbar,
// same ThemeToggle, same studio-content wrapper. Content is a date-picked
// summary with KPI cards, dimension breakdown, session table, and trend.
//
// Access: gated internalOnly at the route level (App.jsx); the backend
// additionally checks is_internal() on every endpoint.

import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  SpaceBetween, Container, Header, Box, Button, Table, StatusIndicator,
  Spinner, Alert, DatePicker, ColumnLayout, LineChart, Tabs,
} from "@cloudscape-design/components";
import ThemeToggle from "../image-studio/ThemeToggle.jsx";
import { useAuth } from "../../auth/AuthProvider.jsx";
import { aiChatApi } from "./aiChatApi.js";
import "./ai-chat.css";

export default function AgentAuditReport() {
  const { username } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(getYesterday());
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    loadSummary(selectedDate);
    loadTrend();
    loadFeedback();
  }, []);

  useEffect(() => {
    loadSummary(selectedDate);
  }, [selectedDate]);

  async function loadSummary(date) {
    setLoading(true);
    setError(null);
    try {
      const data = await aiChatApi.getAuditSummary(date);
      setSummary(data);
    } catch (e) {
      if (e.message.includes("404") || e.message.includes("No audit data")) {
        setSummary(null);
        setError(`No audit data available for ${date}`);
      } else {
        setError(e.message);
      }
    } finally {
      setLoading(false);
    }
  }

  async function loadTrend() {
    try {
      const data = await aiChatApi.getAuditTrend(14);
      setTrend(data.trend || []);
    } catch (e) {
      console.warn("Failed to load trend:", e.message);
    }
  }

  async function loadFeedback() {
    try {
      const data = await aiChatApi.getAuditFeedback();
      setFeedback(data);
    } catch (e) {
      console.warn("Failed to load feedback:", e.message);
    }
  }

  return (
    <div className="image-studio-page ai-chat-knowledge-admin-page">
      <div className="studio-container">
        <ThemeToggle />
        <Link to="/ai-chat/knowledge" className="studio-back-link" aria-label="Back to Knowledge Base">
          ← Back to Knowledge Base
        </Link>

        <div className="studio-hero">
          <div className="studio-hero-left">
            <div className="studio-hero-icon">📊</div>
            <div className="studio-hero-text">
              <h1 className="studio-hero-title">Agent Audit Report</h1>
              <p className="studio-hero-subtitle">
                Daily automated evaluation of AI Chat agent responses — correctness,
                hallucination, context handling, and user satisfaction
              </p>
            </div>
          </div>
        </div>

        <div className="studio-content">
          {/* Date picker row — hidden on User Feedback tab (all-time, not date-scoped) */}
          {activeTab !== "feedback" && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <span style={{ fontWeight: 600, fontSize: 14 }}>Audit date:</span>
              <DatePicker
                value={selectedDate}
                onChange={({ detail }) => setSelectedDate(detail.value)}
                placeholder="YYYY-MM-DD"
              />
              <Button onClick={() => loadSummary(selectedDate)} iconName="refresh" variant="icon" />
            </div>
          )}

          {error && <Alert type="warning" dismissible onDismiss={() => setError(null)}>{error}</Alert>}
          {loading && <Box textAlign="center" padding="xl"><Spinner size="large" /></Box>}

          {!loading && summary && (
            <Tabs
              activeTabId={activeTab}
              onChange={({ detail }) => setActiveTab(detail.activeTabId)}
              tabs={[
                {
                  id: "overview",
                  label: "📈 Overview",
                  content: <OverviewTab summary={summary} trend={trend} feedback={feedback} />,
                },
                {
                  id: "sessions",
                  label: `📋 Sessions (${summary.total_sessions || 0})`,
                  content: <SessionsTab sessions={summary.session_summaries || []} auditDate={selectedDate} />,
                },
                {
                  id: "issues",
                  label: `⚠️ Issues (${(summary.flag_count || 0) + (summary.fail_count || 0)})`,
                  content: <IssuesTab issues={summary.issues || []} auditDate={selectedDate} />,
                },
                {
                  id: "feedback",
                  label: `👎 User Feedback (${feedback?.thumbs_down || 0})`,
                  content: <UserFeedbackTab feedback={feedback} />,
                },
              ]}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Tab: Overview ───────────────────────────────────────────────────────

function OverviewTab({ summary, trend, feedback }) {
  return (
    <SpaceBetween size="l">
      {/* KPI cards */}
      <ColumnLayout columns={4}>
        <MetricCard
          title="Pass Rate"
          value={`${summary.pass_rate}%`}
          color={summary.pass_rate >= 80 ? "#16a34a" : summary.pass_rate >= 60 ? "#d97706" : "#dc2626"}
          description="% of agent responses rated correct with no issues"
        />
        <MetricCard title="Total Messages" value={summary.total_turns || 0} description="Total user↔agent messages exchanged and evaluated" />
        <MetricCard title="Flagged" value={summary.flag_count || 0} color="#d97706" description="Partially correct or minor issues found" />
        <MetricCard title="Failed" value={summary.fail_count || 0} color="#dc2626" description="Incorrect, hallucinated, or misunderstood" />
      </ColumnLayout>

      {/* User Feedback summary cards */}
      {feedback && feedback.total > 0 && (
        <Container header={<Header variant="h3">User Feedback (all time)</Header>}>
          <ColumnLayout columns={4}>
            <MetricCard title="Total Ratings" value={feedback.total} description="Total 👍/👎 ratings given by users" />
            <MetricCard title="👍 Thumbs Up" value={feedback.thumbs_up} color="#16a34a" description="User found the response helpful" />
            <MetricCard title="👎 Thumbs Down" value={feedback.thumbs_down} color="#dc2626" description="User found the response unhelpful or wrong" />
            <MetricCard
              title="Satisfaction Rate"
              value={`${feedback.satisfaction_rate}%`}
              color={feedback.satisfaction_rate >= 80 ? "#16a34a" : feedback.satisfaction_rate >= 60 ? "#d97706" : "#dc2626"}
              description="% of ratings that are thumbs up"
            />
          </ColumnLayout>
        </Container>
      )}

      {/* Session-level stats */}
      <ColumnLayout columns={3}>
        <MetricCard title="Sessions Clean" value={summary.sessions_clean || 0} color="#16a34a" description="All messages in the session were correct" />
        <MetricCard title="Sessions Flagged" value={summary.sessions_flagged || 0} color="#d97706" description="At least one message had a minor issue" />
        <MetricCard title="Sessions Failed" value={summary.sessions_failed || 0} color="#dc2626" description="At least one message had a critical failure" />
      </ColumnLayout>

      {/* Per-dimension breakdown */}
      <Container header={<Header variant="h3">Quality Dimensions</Header>}>
        <DimensionBreakdown dimensions={summary.dimensions} total={summary.total_turns || 0} />
      </Container>

      {/* Trend chart */}
      {trend.length > 1 && (
        <Container header={<Header variant="h3">Pass Rate Trend (last 14 days)</Header>}>
          <LineChart
            series={[
              {
                title: "Pass rate %",
                type: "line",
                data: trend.map(d => ({ x: d.date, y: d.pass_rate })),
              },
            ]}
            xScaleType="categorical"
            yDomain={[0, 100]}
            xTitle="Date"
            yTitle="Pass rate %"
            height={200}
            hideFilter
            hideLegend
          />
        </Container>
      )}
    </SpaceBetween>
  );
}

// ── Tab: Sessions ───────────────────────────────────────────────────────

function SessionsTab({ sessions, auditDate }) {
  const [expandedSession, setExpandedSession] = useState(null);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  if (!sessions || sessions.length === 0) {
    return <Alert type="info">No sessions evaluated.</Alert>;
  }

  async function handleSessionClick(sessionId) {
    if (expandedSession === sessionId) {
      setExpandedSession(null);
      setSessionDetail(null);
      return;
    }
    setExpandedSession(sessionId);
    setDetailLoading(true);
    try {
      const data = await aiChatApi.getAuditDetails(auditDate);
      const results = data.results || [];
      const match = results.find(r => r.session_id === sessionId);
      setSessionDetail(match);
    } catch (e) {
      console.warn("Failed to load session detail:", e.message);
      setSessionDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }

  return (
    <SpaceBetween size="m">
      <Table
        items={sessions}
        columnDefinitions={[
          {
            id: "status",
            header: "Status",
            width: 100,
            cell: item => {
              const type = item.session_overall === "pass" ? "success"
                : item.session_overall === "flag" ? "warning" : "error";
              return <StatusIndicator type={type}>{item.session_overall}</StatusIndicator>;
            },
          },
          { id: "username", header: "User", width: 120, cell: item => item.username || "-" },
          { id: "turns", header: "Messages", width: 90, cell: item => item.total_turns || 0 },
          {
            id: "results",
            header: "Results",
            width: 180,
            cell: item => (
              <span>
                <span style={{ color: "#16a34a" }}>{item.pass_count} pass</span>
                {item.flag_count > 0 && <span style={{ color: "#d97706" }}> · {item.flag_count} flag</span>}
                {item.fail_count > 0 && <span style={{ color: "#dc2626" }}> · {item.fail_count} fail</span>}
              </span>
            ),
          },
          {
            id: "summary",
            header: "Summary",
            cell: item => item.session_summary || "-",
          },
          {
            id: "action",
            header: "Details",
            width: 90,
            cell: item => (
              <Button variant="inline-link" onClick={() => handleSessionClick(item.session_id)}>
                {expandedSession === item.session_id ? "Close" : "Details"}
              </Button>
            ),
          },
        ]}
        variant="embedded"
        wrapLines
        empty="No sessions"
      />

      {/* Expanded session detail */}
      {expandedSession && (
        <Container header={<Header variant="h3">Message-level evaluation</Header>}>
          {detailLoading && <Box textAlign="center"><Spinner /></Box>}
          {!detailLoading && sessionDetail && (
            <TurnDetailTable turns={sessionDetail.turn_results || []} />
          )}
          {!detailLoading && !sessionDetail && (
            <Alert type="info">No detailed evaluation data available for this session. Re-run the audit to populate message content.</Alert>
          )}
        </Container>
      )}
    </SpaceBetween>
  );
}

function TurnDetailTable({ turns }) {
  if (!turns || turns.length === 0) {
    return <Box color="text-body-secondary">No turn data available.</Box>;
  }

  return (
    <SpaceBetween size="l">
      {turns.map((turn, idx) => (
        <Container key={idx}>
          <SpaceBetween size="s">
            {/* Header row */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, borderBottom: "1px solid var(--color-border-divider-default, #e2e8f0)", paddingBottom: 8 }}>
              <StatusIndicator type={turn.overall === "pass" ? "success" : turn.overall === "flag" ? "warning" : "error"}>
                {turn.overall}
              </StatusIndicator>
              <span style={{ fontWeight: 700, fontSize: 14 }}>Exchange {idx + 1}</span>
            </div>

            {/* Evaluator reasoning — highlighted */}
            <div style={{ background: "var(--color-background-layout-toggle-active, #f0fdf4)", padding: "8px 12px", borderRadius: 6, borderLeft: "3px solid #16a34a" }}>
              <Box fontSize="body-s"><strong>💬 Evaluator:</strong> {turn.reasoning || "No reasoning provided"}</Box>
            </div>

            {/* Dimension badges */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              <DimensionBadge label="Correctness" value={turn.correctness} />
              <DimensionBadge label="Hallucination" value={turn.hallucination} />
              <DimensionBadge label="Context" value={turn.context_handling} />
              <DimensionBadge label="Completeness" value={turn.completeness} />
              <DimensionBadge label="Time Period" value={turn.time_period} />
              <DimensionBadge label="Satisfaction" value={turn.user_satisfaction} />
            </div>

            {/* User question */}
            {turn.user_question && (
              <div style={{ background: "var(--color-background-container-content, #f8fafc)", padding: "10px 14px", borderRadius: 8, border: "1px solid var(--color-border-divider-default, #e2e8f0)" }}>
                <Box fontSize="body-s" fontWeight="bold" margin={{ bottom: "xxs" }}>👤 User</Box>
                <Box fontSize="body-m">{turn.user_question}</Box>
              </div>
            )}

            {/* Agent response */}
            {turn.agent_response && (
              <div style={{ background: "var(--color-background-container-content, #f1f5f9)", padding: "10px 14px", borderRadius: 8, border: "1px solid var(--color-border-divider-default, #e2e8f0)" }}>
                <Box fontSize="body-s" fontWeight="bold" margin={{ bottom: "xxs" }}>🤖 Agent</Box>
                <div
                  className="ai-chat-audit-agent-response"
                  style={{ fontSize: 13, lineHeight: 1.5 }}
                  dangerouslySetInnerHTML={{ __html: markdownToHtml(turn.agent_response) }}
                />
              </div>
            )}

            {/* SQL traces */}
            {turn.sql_traces && turn.sql_traces.length > 0 && (
              <div style={{ margin: "8px 0" }}>
                <Box fontSize="body-s" fontWeight="bold" margin={{ bottom: "xxs" }}>🔍 SQL executed</Box>
                {turn.sql_traces.map((t, i) => (
                  <div key={i} style={{ marginBottom: 8 }}>
                    <span style={{ fontSize: 11, color: "#6b7280" }}>({t.row_count} rows returned)</span>
                    <pre style={{
                      background: "#1e293b", color: "#e2e8f0", padding: "10px 14px",
                      borderRadius: 6, overflow: "auto", fontSize: 12,
                      lineHeight: 1.5, margin: "4px 0", whiteSpace: "pre-wrap", wordBreak: "break-word"
                    }}>{t.sql}</pre>
                  </div>
                ))}
              </div>
            )}
          </SpaceBetween>
        </Container>
      ))}
    </SpaceBetween>
  );
}

function DimensionBadge({ label, value }) {
  const isGood = ["correct", "complete", "none", "yes", "satisfied", "not_applicable"].includes(value);
  const color = isGood ? "#16a34a" : value === "cannot_determine" || value === "unclear" ? "#6b7280" : "#d97706";
  return (
    <span style={{
      fontSize: 11, padding: "2px 8px", borderRadius: 12,
      background: isGood ? "#f0fdf4" : "#fef3c7",
      border: `1px solid ${color}33`,
      color,
    }}>
      {label}: {value || "—"}
    </span>
  );
}

// ── Tab: Issues ─────────────────────────────────────────────────────────

function IssuesTab({ issues, auditDate }) {
  const [expandedIssue, setExpandedIssue] = useState(null);
  const [issueDetail, setIssueDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!issues || issues.length === 0) {
    return <Alert type="success">All messages passed — no issues to review.</Alert>;
  }

  async function handleIssueClick(issue) {
    const key = `${issue.session_id}#${issue.seq}`;
    if (expandedIssue === key) {
      setExpandedIssue(null);
      setIssueDetail(null);
      return;
    }
    setExpandedIssue(key);
    setLoading(true);
    try {
      const data = await aiChatApi.getAuditDetails(auditDate);
      const results = data.results || [];
      const session = results.find(r => r.session_id === issue.session_id);
      if (session && session.turn_results) {
        const turn = session.turn_results.find(t => parseInt(t.seq) === parseInt(issue.seq));
        setIssueDetail(turn || null);
      }
    } catch (e) {
      console.warn("Failed to load issue detail:", e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <SpaceBetween size="m">
      {issues.map((issue, idx) => {
        const key = `${issue.session_id}#${issue.seq}`;
        const isExpanded = expandedIssue === key;
        return (
          <Container key={idx} variant="stacked">
            <SpaceBetween size="xs">
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <StatusIndicator type={issue.overall === "fail" ? "error" : "warning"}>
                  {issue.overall}
                </StatusIndicator>
                <span style={{ fontWeight: 600 }}>{issue.username}</span>
                <span style={{ fontSize: 12, color: "#6b7280" }}>Message #{issue.seq}</span>
                <Button variant="inline-link" onClick={() => handleIssueClick(issue)}>
                  {isExpanded ? "Close" : "View details"}
                </Button>
              </div>

              <Box fontSize="body-s" color="text-body-secondary">
                💬 {issue.reasoning || "No reasoning"}
              </Box>

              {isExpanded && loading && <Spinner />}
              {isExpanded && !loading && issueDetail && (
                <Box padding={{ top: "s" }}>
                  {issueDetail.user_question && (
                    <Box margin={{ bottom: "xs" }}>
                      <Box fontSize="body-s" fontWeight="bold" color="text-label">👤 User:</Box>
                      <Box fontSize="body-s" padding={{ left: "m" }}>{issueDetail.user_question}</Box>
                    </Box>
                  )}
                  {issueDetail.agent_response && (
                    <Box margin={{ bottom: "xs" }}>
                      <Box fontSize="body-s" fontWeight="bold" color="text-label">🤖 Agent:</Box>
                      <Box fontSize="body-s" padding={{ left: "m" }}>{issueDetail.agent_response}</Box>
                    </Box>
                  )}
                  {issueDetail.sql_traces && issueDetail.sql_traces.length > 0 && (
                    <Box>
                      <Box fontSize="body-s" fontWeight="bold" color="text-label">🔍 SQL:</Box>
                      {issueDetail.sql_traces.map((t, i) => (
                        <Box key={i} fontSize="body-s" padding={{ left: "m" }} color="text-body-secondary">
                          <code>({t.row_count} rows) {t.sql}</code>
                        </Box>
                      ))}
                    </Box>
                  )}
                  {!issueDetail.user_question && !issueDetail.agent_response && (
                    <Alert type="info">Message content not available. Re-run the audit to populate.</Alert>
                  )}
                  <Box fontSize="body-s" color="text-body-secondary" margin={{ top: "xs" }}>
                    correctness: {issueDetail.correctness} · completeness: {issueDetail.completeness} · hallucination: {issueDetail.hallucination} · context: {issueDetail.context_handling} · satisfaction: {issueDetail.user_satisfaction}
                  </Box>
                </Box>
              )}
            </SpaceBetween>
          </Container>
        );
      })}
    </SpaceBetween>
  );
}

// ── Tab: User Feedback ───────────────────────────────────────────────────

function UserFeedbackTab({ feedback }) {
  if (!feedback || feedback.thumbs_down === 0) {
    return <Alert type="success">No negative feedback received — all rated responses were helpful.</Alert>;
  }

  const items = feedback.down_details || [];

  return (
    <SpaceBetween size="m">
      <Box fontSize="body-s" color="text-body-secondary">
        Showing {items.length} thumbs-down rating{items.length !== 1 ? "s" : ""} (most recent first)
      </Box>
      {items.map((item, idx) => (
        <Container key={idx}>
          <SpaceBetween size="xs">
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, borderBottom: "1px solid var(--color-border-divider-default, #e2e8f0)", paddingBottom: 8 }}>
              <span style={{ fontSize: 18 }}>👎</span>
              <span style={{ fontWeight: 600 }}>{item.username}</span>
              <span style={{ fontSize: 12, color: "#6b7280" }}>{formatDate(item.created_at)}</span>
              <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: "auto" }}>
                Session: <code style={{ fontSize: 11 }}>{item.session_id}</code> · Message #{item.seq}
              </span>
            </div>

            {/* User comment (if any) */}
            {item.comment && (
              <div style={{ background: "#fef2f2", padding: "8px 12px", borderRadius: 6, borderLeft: "3px solid #dc2626" }}>
                <Box fontSize="body-s"><strong>User's comment:</strong> {item.comment}</Box>
              </div>
            )}

            {/* User question */}
            {item.user_question && (
              <div style={{ background: "var(--color-background-container-content, #f8fafc)", padding: "10px 14px", borderRadius: 8, border: "1px solid var(--color-border-divider-default, #e2e8f0)" }}>
                <Box fontSize="body-s" fontWeight="bold" margin={{ bottom: "xxs" }}>👤 User asked:</Box>
                <Box fontSize="body-m">{item.user_question}</Box>
              </div>
            )}

            {/* Agent response */}
            {item.agent_response && (
              <div style={{ background: "var(--color-background-container-content, #f1f5f9)", padding: "10px 14px", borderRadius: 8, border: "1px solid var(--color-border-divider-default, #e2e8f0)" }}>
                <Box fontSize="body-s" fontWeight="bold" margin={{ bottom: "xxs" }}>🤖 Agent responded:</Box>
                <div
                  style={{ fontSize: 13, lineHeight: 1.5 }}
                  dangerouslySetInnerHTML={{ __html: markdownToHtml(item.agent_response) }}
                />
              </div>
            )}

            {!item.user_question && !item.comment && (
              <Box fontSize="body-s" color="text-body-secondary">No additional context available for this feedback.</Box>
            )}
          </SpaceBetween>
        </Container>
      ))}
    </SpaceBetween>
  );
}

// ── Shared components ───────────────────────────────────────────────────

function MetricCard({ title, value, color, description }) {
  return (
    <Container>
      <Box textAlign="center">
        <Box fontSize="heading-s" color="text-label">{title}</Box>
        <Box fontSize="display-l" fontWeight="bold">
          <span style={color ? { color } : undefined}>{value}</span>
        </Box>
        {description && (
          <Box fontSize="body-s" color="text-body-secondary" margin={{ top: "xxs" }}>
            {description}
          </Box>
        )}
      </Box>
    </Container>
  );
}

function DimensionBreakdown({ dimensions, total }) {
  if (!dimensions || total === 0) return <Box color="text-body-secondary">No data</Box>;

  const DIMENSION_LABELS = {
    correctness: "Correctness",
    completeness: "Completeness",
    hallucination: "Hallucination",
    time_period: "Time Period",
    question_understood: "Question Understood",
    context_handling: "Context Handling",
    user_satisfaction: "User Satisfaction",
  };

  const DIMENSION_DESCRIPTIONS = {
    correctness: "Is the answer factually right given the SQL results and the question asked?",
    completeness: "Did the agent answer all parts of what the user asked?",
    hallucination: "Did the agent claim facts not supported by the actual query data?",
    time_period: "Did the agent use the correct date/week/month range the user asked for?",
    question_understood: "Did the agent correctly interpret what the user meant, including follow-ups?",
    context_handling: "For follow-up questions, did the agent correctly carry forward prior context (filters, tables, metrics)?",
    user_satisfaction: "Based on the user's next message — did they accept the answer or correct the agent?",
  };

  const GOOD_VALUES = {
    correctness: "correct",
    completeness: "complete",
    hallucination: "none",
    time_period: "correct",
    question_understood: "yes",
    context_handling: "correct",
    user_satisfaction: "satisfied",
  };

  return (
    <ColumnLayout columns={3}>
      {Object.entries(dimensions).map(([dim, counts]) => {
        const goodVal = GOOD_VALUES[dim];
        const goodCount = counts[goodVal] || 0;
        const goodPct = total > 0 ? Math.round((goodCount / total) * 100) : 0;
        const color = goodPct >= 80 ? "text-status-success" : goodPct >= 60 ? "text-status-warning" : "text-status-error";
        return (
          <Box key={dim} padding="s">
            <Box fontWeight="bold" fontSize="body-s">{DIMENSION_LABELS[dim] || dim}</Box>
            <Box fontSize="body-s" color="text-body-secondary" margin={{ bottom: "xxs" }}>
              {DIMENSION_DESCRIPTIONS[dim] || ""}
            </Box>
            <Box fontSize="heading-m" color={color}>
              {goodPct}% {goodVal}
            </Box>
            <Box fontSize="body-s" color="text-body-secondary">
              {Object.entries(counts).map(([val, cnt]) => `${val}: ${cnt}`).join(" · ")}
            </Box>
          </Box>
        );
      })}
    </ColumnLayout>
  );
}

// ── Utilities ───────────────────────────────────────────────────────────

/**
 * Lightweight markdown→HTML for agent responses in the audit view.
 * Handles: tables, headings, bold, bullet/numbered lists, code blocks.
 * Same block-parser approach as AiChat.jsx's markdownToHtml.
 */
function markdownToHtml(text) {
  if (!text) return "";
  const lines = text.split("\n");
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block
    if (line.trim().startsWith("```")) {
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith("```")) {
        codeLines.push(escapeHtml(lines[i]));
        i++;
      }
      i++; // skip closing ```
      blocks.push(`<pre style="background:#1e293b;color:#e2e8f0;padding:8px 12px;border-radius:6px;overflow-x:auto;font-size:12px">${codeLines.join("\n")}</pre>`);
      continue;
    }

    // Table (detect by |---|)
    if (line.includes("|") && i + 1 < lines.length && /^\s*\|[\s\-:|]+\|\s*$/.test(lines[i + 1])) {
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        tableLines.push(lines[i]);
        i++;
      }
      blocks.push(renderTable(tableLines));
      continue;
    }
    // Also catch if current line IS the separator (header was previous block line)
    if (/^\s*\|[\s\-:|]+\|\s*$/.test(line) && blocks.length > 0) {
      // separator without header context — skip
      i++;
      continue;
    }
    // Table row starting with | but separator might be this line
    if (line.trim().startsWith("|") && line.includes("|")) {
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        tableLines.push(lines[i]);
        i++;
      }
      if (tableLines.length >= 2) {
        blocks.push(renderTable(tableLines));
      } else {
        blocks.push(`<p>${inlineMarkdown(tableLines[0])}</p>`);
      }
      continue;
    }

    // Heading
    if (/^#{1,4}\s/.test(line)) {
      const level = line.match(/^(#{1,4})/)[1].length;
      const text = line.replace(/^#{1,4}\s+/, "");
      blocks.push(`<h${level + 1} style="margin:8px 0 4px;font-size:${16 - level}px">${inlineMarkdown(text)}</h${level + 1}>`);
      i++;
      continue;
    }

    // Bullet list
    if (/^\s*[-*]\s/.test(line)) {
      const listItems = [];
      while (i < lines.length && /^\s*[-*]\s/.test(lines[i])) {
        listItems.push(lines[i].replace(/^\s*[-*]\s+/, ""));
        i++;
      }
      blocks.push(`<ul style="margin:4px 0;padding-left:20px">${listItems.map(li => `<li>${inlineMarkdown(li)}</li>`).join("")}</ul>`);
      continue;
    }

    // Numbered list
    if (/^\s*\d+\.\s/.test(line)) {
      const listItems = [];
      while (i < lines.length && /^\s*\d+\.\s/.test(lines[i])) {
        listItems.push(lines[i].replace(/^\s*\d+\.\s+/, ""));
        i++;
      }
      blocks.push(`<ol style="margin:4px 0;padding-left:20px;list-style-type:decimal">${listItems.map(li => `<li style="margin-bottom:6px">${inlineMarkdown(li)}</li>`).join("")}</ol>`);
      continue;
    }

    // Empty line
    if (!line.trim()) {
      i++;
      continue;
    }

    // Regular paragraph
    blocks.push(`<p style="margin:4px 0">${inlineMarkdown(line)}</p>`);
    i++;
  }

  return blocks.join("");
}

function renderTable(tableLines) {
  // Filter out separator lines
  const rows = tableLines.filter(l => !/^\s*\|[\s\-:|]+\|\s*$/.test(l));
  if (rows.length === 0) return "";

  const parseRow = (row) => row.split("|").slice(1, -1).map(c => c.trim());
  const headerCells = parseRow(rows[0]);
  const bodyRows = rows.slice(1);

  let html = '<div style="overflow-x:auto;margin:8px 0"><table style="border-collapse:collapse;font-size:12px;width:100%">';
  html += "<thead><tr>";
  headerCells.forEach(c => { html += `<th style="border:1px solid #d1d5db;padding:4px 8px;background:#f1f5f9;text-align:left;white-space:nowrap">${inlineMarkdown(c)}</th>`; });
  html += "</tr></thead><tbody>";
  bodyRows.forEach((row, idx) => {
    const cells = parseRow(row);
    const bg = idx % 2 === 0 ? "" : "background:#f8fafc;";
    html += `<tr style="${bg}">`;
    cells.forEach(c => { html += `<td style="border:1px solid #d1d5db;padding:4px 8px;white-space:nowrap">${inlineMarkdown(c)}</td>`; });
    html += "</tr>";
  });
  html += "</tbody></table></div>";
  return html;
}

function inlineMarkdown(text) {
  if (!text) return "";
  let s = escapeHtml(text);
  s = s.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  s = s.replace(/\*(.+?)\*/g, "<em>$1</em>");
  s = s.replace(/`(.+?)`/g, '<code style="background:#e2e8f0;padding:1px 4px;border-radius:3px;font-size:11px">$1</code>');
  return s;
}

function escapeHtml(text) {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function getYesterday() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split("T")[0];
}

function formatDate(isoStr) {
  if (!isoStr) return "";
  try {
    const d = new Date(isoStr);
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return isoStr.split("T")[0];
  }
}
